import { paymentProcessorTitle } from "../../../../../Utils/utils"
import SECTION_SCHEMA, {
  getSectionKeys,
  getSectionLabel,
  isExperimentalContentStep,
  resolveStepFields,
  SECTION_LABELS
} from "../schema/sections"
import { evalShowIf, visibleQuestions } from "./showIf"

// Turns the saved building blocks into a flat, human-readable read-out of every
// answered field, section by section, for the one-page Configure summary (flow v3).
// Only VISIBLE (showIf-passing) questions with an actual value are included, so the
// read-out shows exactly what the merchant entered, in the wizard's own order, with no
// blank rows. Pure aside from `t` for label/option translation (mirrors how
// categoryGroups.groupByNetworkCategory takes t).

const getDeep = (obj, path) => {
  if (!path || !obj) return undefined
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

// Resolve a select / chip-multi option value to its (i18n-key) label.
const optionLabel = (q, value) => {
  const opt = (q.options || []).find((o) => o.value === value)
  return opt ? opt.label : value
}

// Title-case a slug fallback ("custom_mcp" -> "Custom mcp") for connected tools that
// have no friendlier label to hand.
const titleize = (slug) =>
  String(slug || "")
    .replace(/_/g, " ")
    .replace(/\b\w/, (c) => c.toUpperCase())

const procTitle = (slug) => paymentProcessorTitle[slug] || titleize(slug)

// One question -> a display string, or null when there's nothing to show.
const formatValue = (q, sectionState, buildingBlocks, t) => {
  const raw = getDeep(sectionState, q.key)
  switch (q.type) {
    case "chip-multi": {
      const labels = (Array.isArray(raw) ? raw : []).map((v) => t(optionLabel(q, v)))
      const customs = getDeep(sectionState, `${q.key}_custom`)
      if (Array.isArray(customs)) labels.push(...customs)
      return labels.length ? labels.join(", ") : null
    }
    case "text-multi": {
      const arr = Array.isArray(raw)
        ? raw.slice()
        : typeof raw === "string" && raw.trim()
          ? [raw]
          : []
      // Descriptors auto-verified from the connected processor live on processors[slug].
      const procs = buildingBlocks?.processors
      if (procs && Array.isArray(procs.connected)) {
        procs.connected.forEach((slug) => {
          const d = procs[slug] && procs[slug].statement_descriptor
          if (d && !arr.includes(d)) arr.push(d)
        })
      }
      return arr.length ? arr.join(", ") : null
    }
    case "select":
      if (raw === undefined || raw === null || raw === "") return null
      return t(optionLabel(q, raw))
    case "number": {
      if (raw === "" || raw === undefined || raw === null) return null
      const n = Number(raw)
      if (Number.isNaN(n)) return null
      if (n === 0 && !q.acceptZero) return null
      return String(raw)
    }
    case "phone": {
      if (!raw) return null
      if (typeof raw === "object")
        return `${raw.code || raw.dialCode || ""} ${raw.number || raw.phone || ""}`.trim() || null
      return String(raw)
    }
    case "upload":
      return raw && (raw.s3_key || raw.url) ? t("Provided") : null
    default: {
      // text / url / textarea
      if (typeof raw === "string") return raw.trim() || null
      return raw != null && raw !== "" ? String(raw) : null
    }
  }
}

// [{ key, label, items: [{ label, value, missing }] }] for every section that has at
// least one answered field OR an empty REQUIRED field. Answered questions show their
// value; a required question left blank stays in the list as a highlighted reminder
// (missing: true) instead of silently disappearing. The two connect steps (Integrate /
// Enrichment) have no form schema, so their "answers" are the connected processors /
// tools; a gateway is required for activation, so an empty Integrate step is also
// surfaced as missing. Enrichment stays optional (omitted when empty).
export const buildConfigureReadout = (buildingBlocks, t) => {
  const out = []
  for (const key of getSectionKeys()) {
    if (key === "integrations") {
      const connected = buildingBlocks?.processors?.connected || []
      out.push({
        key,
        label: t(getSectionLabel(key)),
        items: connected.length
          ? [{ label: t("Connected processors"), value: connected.map(procTitle).join(", ") }]
          : [{ label: t("Connected processors"), missing: true }]
      })
      continue
    }
    if (key === "enrichment") {
      const tools = buildingBlocks?.enrichment?.connected || []
      if (tools.length) {
        out.push({
          key,
          label: t(getSectionLabel(key)),
          items: [{ label: t("Connected tools"), value: tools.map(titleize).join(", ") }]
        })
      }
      continue
    }
    // EXPERIMENTAL content step: aggregate its fields across buckets, each read from
    // its own bucket state, in display order. Same answered / missing-required logic.
    if (isExperimentalContentStep(key)) {
      const items = []
      for (const f of resolveStepFields(key)) {
        if (!evalShowIf(f.def.showIf, buildingBlocks)) continue
        const sState = buildingBlocks?.[f.section] ?? {}
        const value = formatValue(f.def, sState, buildingBlocks, t)
        if (value != null && value !== "") items.push({ label: t(f.def.label), value })
        else if (f.def.required) items.push({ label: t(f.def.label), missing: true })
      }
      if (items.length) out.push({ key, label: t(getSectionLabel(key)), items })
      continue
    }
    const questions = visibleQuestions(SECTION_SCHEMA[key] || [], buildingBlocks)
    const sectionState = buildingBlocks?.[key] ?? {}
    const items = []
    for (const q of questions) {
      const value = formatValue(q, sectionState, buildingBlocks, t)
      if (value != null && value !== "") items.push({ label: t(q.label), value })
      else if (q.required) items.push({ label: t(q.label), missing: true })
    }
    if (items.length) out.push({ key, label: t(SECTION_LABELS[key]), items })
  }
  return out
}
